import type { BlobLike } from "@pdfking/compatibility"
import type {
  GetBlockPointer,
  IsDataAvailPointer,
  PageNumber,
  PdfiumAPI,
  PdfReaderPointer,
} from "@pdfking/pdfium"
import type { Bytes } from "@pdfking/tfunc"
import { BlobArray, BlobToUint8 } from "@pdfking/compatibility"
import { ArrayToIterable, IterableToArray } from "@pdfking/tfunc"
import { AutoThrow } from "./AutoThrow"

const MEM_THRESHOLD = (30 * 1024 * 1024) as Bytes

export type OpenReaderResult = {
  readonly handle: PdfReaderPointer
  readonly page_count: number
  readonly ensure_page: (page_index: number) => Promise<void>
  readonly close: () => Promise<void>
}

const OpenReaderMem = async (
  api: PdfiumAPI,
  stream: AsyncIterable<BlobLike> | Iterable<BlobLike>
): Promise<OpenReaderResult> => {
  const handle = AutoThrow(
    await IterableToArray(stream)
      .then(BlobArray)
      .then(BlobToUint8)
      .then(data => api.open_mem(data))
  )
  const count = Number(await api.page_count(handle))

  return {
    handle,
    page_count: count,
    ensure_page: async () => {},
    close: async () => {
      AutoThrow(await api.close_mem(handle))
    },
  }
}

const OpenReaderStream = async (
  api: PdfiumAPI,
  stream: AsyncIterable<BlobLike> | Iterable<BlobLike>,
  size: Bytes
): Promise<OpenReaderResult> => {
  const blob = BlobArray()
  const iter = ArrayToIterable(stream)
  const { instance } = api

  const pump = (): Promise<boolean> =>
    iter.next().then(({ done, value }) => {
      if (value) blob.push(value)
      return !done
    })

  const getBlock = (
    _param: number | bigint,
    position: number | bigint,
    bufPtr: number | bigint,
    sz: number | bigint
  ): number => {
    const pos = Number(position)
    const size = Number(sz)
    if (pos + size > blob.size) return 0
    const data = new Uint8Array(blob.slice(pos, pos + size).arrayBufferSync())
    instance.write(Number(bufPtr), data)
    return 1
  }

  const isDataAvail = (
    _pThis: number | bigint,
    offset: number | bigint,
    sz: number | bigint
  ): number => {
    return Number(offset) + Number(sz) <= blob.size ? 1 : 0
  }

  const p = instance.memory64 ? "j" : "i"
  const getBlockCb = instance.addCallback(getBlock, `i${p}${p}${p}${p}`)
  const isDataAvailCb = instance.addCallback(isDataAvail, `i${p}${p}${p}`)

  const cleanup = () => {
    getBlockCb.free()
    isDataAvailCb.free()
    blob.clear()
  }

  try {
    const handle = AutoThrow(
      await api.open(
        size,
        getBlockCb.pointer as unknown as GetBlockPointer,
        isDataAvailCb.pointer as unknown as IsDataAvailPointer
      )
    )

    for (;;) {
      if (AutoThrow(await api.doc_avail(handle))) break
      if (!(await pump())) break
    }

    const count = Number(await api.page_count(handle))

    const ensure_page = async (page_index: number): Promise<void> => {
      for (;;) {
        if (AutoThrow(await api.page_avail(handle, page_index as PageNumber)))
          break
        if (!(await pump())) break
      }
    }

    return {
      handle,
      page_count: count,
      ensure_page,
      close: async () => {
        AutoThrow(await api.close(handle))
        cleanup()
      },
    }
  } catch (e) {
    cleanup()
    throw e
  }
}

export const OpenReader = (
  api: PdfiumAPI,
  stream: AsyncIterable<BlobLike> | Iterable<BlobLike>,
  size: Bytes
): Promise<OpenReaderResult> =>
  size <= MEM_THRESHOLD
    ? OpenReaderMem(api, stream)
    : OpenReaderStream(api, stream, size)
